'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { cn } from '@medizzconnect/shared';
import { useAutoplayUnlock } from '@/lib/use-autoplay-unlock';

const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@medizzconnect';
const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

const SERIES_VIDEOS = [
  {
    id: '9DGQs5gW4IY',
    series: 'HELP 2',
    title: 'medizzconnect® – in 55 Sek erklärt',
    thumbnail: '/images/youtube/help-2-60-sekunden.webp',
  },
  {
    id: 'LKHO1cQftR4',
    series: 'HELP 1',
    title: 'Drei Schritte zum eigenen Profilfilm',
    thumbnail: '/images/youtube/help-1-global-telehealth.webp',
  },
  {
    id: 't215oLiOStA',
    series: 'HUB 1',
    title: 'Achtung: Wie Medien Medizin verändern',
    thumbnail: '/images/youtube/hub-1-arztsuche.webp',
  },
] as const;

interface ScrollState {
  hasOverflow: boolean;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

const INITIAL_SCROLL_STATE: ScrollState = {
  hasOverflow: false,
  canGoPrevious: false,
  canGoNext: false,
};

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
    </svg>
  );
}

function getYouTubeEmbedUrl(videoId: string) {
  const params = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    disablekb: '1',
    enablejsapi: '1',
    fs: '0',
    iv_load_policy: '3',
    loop: '1',
    modestbranding: '1',
    mute: '1',
    playlist: videoId,
    playsinline: '1',
    rel: '0',
  });

  if (typeof window !== 'undefined') {
    params.set('origin', window.location.origin);
  }

  return `${YOUTUBE_EMBED_ORIGIN}/embed/${videoId}?${params.toString()}`;
}

export function YouTubeSeriesCarousel() {
  const t = useTranslations('home.youtubeSeries');
  const locale = useLocale();
  const isRtl = locale === 'ar';
  const scrollRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playerCommandTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const activeVideoIdRef = useRef<string | null>(null);
  const isAudioUnlocked = useAutoplayUnlock();
  const isAudioUnlockedRef = useRef(isAudioUnlocked);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [scrollState, setScrollState] = useState<ScrollState>(INITIAL_SCROLL_STATE);

  useEffect(() => {
    isAudioUnlockedRef.current = isAudioUnlocked;
  }, [isAudioUnlocked]);

  const clearPlayerCommandTimeouts = useCallback(() => {
    playerCommandTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    playerCommandTimeoutsRef.current = [];
  }, []);

  const sendPlayerCommand = useCallback((func: string, args: unknown[] = []) => {
    playerRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      YOUTUBE_EMBED_ORIGIN,
    );
  }, []);

  const syncPlayer = useCallback(
    (videoId: string, withSound: boolean) => {
      if (activeVideoIdRef.current !== videoId) return;
      clearPlayerCommandTimeouts();

      // onLoad kann knapp vor der Player-API-Bereitschaft feuern. Kurze
      // Wiederholungen machen Play/Unmute zuverlässig, ohne die API zu laden.
      [0, 250, 750, 1500, 3000].forEach((delay) => {
        const timeout = setTimeout(() => {
          if (activeVideoIdRef.current !== videoId) return;

          sendPlayerCommand('playVideo');
          if (withSound) {
            sendPlayerCommand('setVolume', [100]);
            sendPlayerCommand('unMute');
          } else {
            sendPlayerCommand('mute');
          }
        }, delay);
        playerCommandTimeoutsRef.current.push(timeout);
      });
    },
    [clearPlayerCommandTimeouts, sendPlayerCommand],
  );

  const stopActiveVideo = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    clearPlayerCommandTimeouts();
    activeVideoIdRef.current = null;
    setActiveVideoId(null);
  }, [clearPlayerCommandTimeouts]);

  const handleVideoMouseEnter = useCallback((videoId: string) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      activeVideoIdRef.current = videoId;
      setActiveVideoId(videoId);
      document.dispatchEvent(
        new CustomEvent('video-card-playing', { detail: { id: `youtube:${videoId}` } }),
      );

      if (!isAudioUnlockedRef.current) {
        document.dispatchEvent(new Event('request-audio-consent'));
      }
    }, 50);
  }, []);

  const handleVideoMouseLeave = useCallback(
    (videoId: string) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (activeVideoIdRef.current === videoId) stopActiveVideo();
    },
    [stopActiveVideo],
  );

  useEffect(() => {
    const onSoundAllowed = () => {
      const videoId = activeVideoIdRef.current;
      if (videoId) syncPlayer(videoId, true);
    };
    const onOtherVideoPlaying = (event: Event) => {
      const currentVideoId = activeVideoIdRef.current;
      if (!currentVideoId) return;

      const { id } = (event as CustomEvent<{ id: string }>).detail;
      if (id !== `youtube:${currentVideoId}`) stopActiveVideo();
    };

    document.addEventListener('sound-allowed', onSoundAllowed);
    document.addEventListener('video-card-playing', onOtherVideoPlaying);
    return () => {
      document.removeEventListener('sound-allowed', onSoundAllowed);
      document.removeEventListener('video-card-playing', onOtherVideoPlaying);
      stopActiveVideo();
    };
  }, [stopActiveVideo, syncPlayer]);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const maxScroll = Math.max(0, element.scrollWidth - element.clientWidth);
    const position = Math.min(maxScroll, Math.abs(element.scrollLeft));
    const nextState: ScrollState = {
      hasOverflow: maxScroll > 2,
      canGoPrevious: position > 2,
      canGoNext: position < maxScroll - 2,
    };

    setScrollState((current) =>
      current.hasOverflow === nextState.hasOverflow &&
      current.canGoPrevious === nextState.canGoPrevious &&
      current.canGoNext === nextState.canGoNext
        ? current
        : nextState,
    );
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const frame = window.requestAnimationFrame(updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollCards = useCallback(
    (direction: 'previous' | 'next') => {
      const element = scrollRef.current;
      if (!element) return;

      const cards = Array.from(
        element.querySelectorAll<HTMLElement>('[data-youtube-series-card]'),
      );
      if (cards.length === 0) return;

      const trackRect = element.getBoundingClientRect();
      const currentIndex = cards.reduce((closestIndex, card, index) => {
        const cardRect = card.getBoundingClientRect();
        const currentRect = cards[closestIndex]?.getBoundingClientRect();
        if (!currentRect) return index;

        const distance = isRtl
          ? Math.abs(trackRect.right - cardRect.right)
          : Math.abs(trackRect.left - cardRect.left);
        const closestDistance = isRtl
          ? Math.abs(trackRect.right - currentRect.right)
          : Math.abs(trackRect.left - currentRect.left);

        return distance < closestDistance ? index : closestIndex;
      }, 0);

      const cardWidth = cards[0]?.getBoundingClientRect().width ?? element.clientWidth;
      const visibleCards = Math.max(1, Math.round(element.clientWidth / cardWidth));
      const offset = direction === 'next' ? visibleCards : -visibleCards;
      const targetIndex = Math.min(cards.length - 1, Math.max(0, currentIndex + offset));

      cards[targetIndex]?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'start',
      });
      updateScrollState();
    },
    [isRtl, updateScrollState],
  );

  const previousIconDirection = isRtl ? 'right' : 'left';
  const nextIconDirection = isRtl ? 'left' : 'right';

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-b from-[#dcebea] via-[#eaf3f2] to-[#f4f7f7] py-12 sm:py-14"
      aria-labelledby="youtube-series-title"
    >
      <div
        className="absolute top-0 left-0 z-[110] w-full h-[3px] md:h-[4px]"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 15%, transparent 30%, transparent 40%, rgb(255, 255, 255) 50%, transparent 60%, transparent 70%, rgba(255, 255, 255, 0.8) 85%, transparent 100%)',
          boxShadow: 'rgba(255, 255, 255, 0.25) 0px 2px 8px',
        }}
        aria-hidden="true"
      />

      <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-8">
        <header className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-700">
            medizzconnect™ · YouTube
          </p>
          <h2
            id="youtube-series-title"
            className="mt-2 text-2xl font-semibold tracking-tight text-text-primary sm:text-3xl"
          >
            {t('title')}
          </h2>
          <p className="mt-3 text-sm leading-6 text-text-secondary sm:text-base">
            {t('description')}
          </p>
        </header>

        <div className="relative mx-auto mt-8 max-w-5xl">
          {scrollState.hasOverflow && (
            <button
              type="button"
              onClick={() => scrollCards('previous')}
              disabled={!scrollState.canGoPrevious}
              className="absolute start-0 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border-default bg-white/95 text-text-secondary shadow-md transition hover:border-primary-400 hover:bg-white hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-35"
              aria-label={t('previous')}
              aria-controls="youtube-series-track"
            >
              <ArrowIcon direction={previousIconDirection} />
            </button>
          )}

          <div
            id="youtube-series-track"
            ref={scrollRef}
            dir={isRtl ? 'rtl' : 'ltr'}
            className={cn(
              'scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto',
              scrollState.hasOverflow && 'mx-12 sm:mx-14',
            )}
            onScroll={updateScrollState}
            aria-label={t('carouselLabel')}
          >
            {SERIES_VIDEOS.map((video) => (
              <article
                key={video.id}
                data-youtube-series-card
                className="group relative flex basis-full shrink-0 snap-start flex-col overflow-hidden rounded-xl bg-white text-start shadow-sm ring-1 ring-black/5 transition duration-300 hover:-translate-y-1 hover:shadow-lg focus-within:outline focus-within:outline-2 focus-within:outline-offset-4 focus-within:outline-primary-500 sm:basis-[calc((100%-1rem)/2)] lg:basis-[calc((100%-2rem)/3)]"
                onMouseEnter={() => handleVideoMouseEnter(video.id)}
                onMouseLeave={() => handleVideoMouseLeave(video.id)}
              >
                <a
                  href={YOUTUBE_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-full flex-1 flex-col"
                  aria-label={t('openChannel', { title: video.title })}
                >
                  <div className="relative aspect-video overflow-hidden bg-secondary-200">
                    <Image
                      src={video.thumbnail}
                      alt={t('thumbnailAlt', { title: video.title })}
                      fill
                      sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 320px"
                      className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/5" />
                    <span className="absolute inset-0 m-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-[#ff0033] shadow-lg transition-transform duration-300 group-hover:scale-110">
                      <YouTubeIcon />
                    </span>
                  </div>

                  <div className="flex min-h-32 flex-1 flex-col bg-white px-4 py-3.5">
                    <span
                      dir="ltr"
                      className="mb-2 w-fit rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-bold tracking-[0.14em] text-primary-700"
                    >
                      {video.series}
                    </span>
                    <h3
                      dir="auto"
                      className="line-clamp-2 text-sm font-semibold leading-5 text-text-primary"
                    >
                      {video.title}
                    </h3>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-3 text-xs font-semibold text-[#d4002a]">
                      <YouTubeIcon />
                      {t('watchOnYouTube')}
                    </span>
                  </div>
                </a>

                {activeVideoId === video.id && (
                  <iframe
                    ref={playerRef}
                    src={getYouTubeEmbedUrl(video.id)}
                    title={video.title}
                    className="pointer-events-none absolute inset-x-0 top-0 z-10 aspect-video w-full border-0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    tabIndex={-1}
                    aria-hidden="true"
                    onLoad={() => syncPlayer(video.id, isAudioUnlockedRef.current)}
                  />
                )}
              </article>
            ))}
          </div>

          {scrollState.hasOverflow && (
            <button
              type="button"
              onClick={() => scrollCards('next')}
              disabled={!scrollState.canGoNext}
              className="absolute end-0 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border-default bg-white/95 text-text-secondary shadow-md transition hover:border-primary-400 hover:bg-white hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-35"
              aria-label={t('next')}
              aria-controls="youtube-series-track"
            >
              <ArrowIcon direction={nextIconDirection} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
